import React, { useState, useEffect } from "react";
import useCmsStore from "../../../services/useCmsStore";
import { toast } from "react-toastify";
import { FiX, FiTag, FiSave } from "react-icons/fi";
import "./Modal.css";

const TagModal = ({ isOpen, onClose, tag = null }) => {
  const { createTag, updateTag } = useCmsStore();
  const isEditing = !!tag;

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    color: "#6B7280",
    seo_title: "",
    seo_description: "",
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name || "",
        slug: tag.slug || "",
        description: tag.description || "",
        color: tag.color || "#6B7280",
        seo_title: tag.seo_title || "",
        seo_description: tag.seo_description || "",
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        description: "",
        color: "#6B7280",
        seo_title: "",
        seo_description: "",
      });
    }
  }, [tag, isOpen]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "name" && !isEditing) {
        newData.slug = generateSlug(value);
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateTag(tag.id, formData);
        toast.success("Tag updated successfully");
      } else {
        await createTag(formData);
        toast.success("Tag created successfully");
      }
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save tag");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cms-modal-overlay" onClick={onClose}>
      <div className="cms-modal cms-modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="cms-modal-header">
          <div className="cms-modal-title">
            <FiTag size={20} />
            <h3>{isEditing ? "Edit Tag" : "New Tag"}</h3>
          </div>
          <button className="cms-modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="cms-modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Tag name"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="tag-slug"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Color</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleChange("color", e.target.value)}
                  className="color-input"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => handleChange("color", e.target.value)}
                  placeholder="#6B7280"
                  className="color-text"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
          </div>

          <div className="cms-modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              <FiSave size={16} />
              <span>{isSaving ? "Saving..." : isEditing ? "Update" : "Create"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TagModal;
